import React, { useState } from 'react';
import { Save, Package } from 'lucide-react';
import { X } from './icons';
import { consumiblesAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import LocationSelector from './LocationSelector';
import EmpresaSelector from './EmpresaSelector';

export default function CrearConsumibleModal({ isOpen, onClose, onCreated }) {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        nombre: '',
        categoria: 'Accesorios',
        descripcion: '',
        stockActual: 0,
        stockMinimo: 5,
        ubicacion: '',
        empresa: '',
        valor: '',
    });

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSelectorChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.nombre.trim()) {
            showToast('El nombre es obligatorio', 'error');
            return;
        }

        const payload = {
            ...formData,
            stockActual: parseInt(formData.stockActual, 10) || 0,
            stockMinimo: parseInt(formData.stockMinimo, 10) || 0,
            valor: formData.valor === '' ? undefined : Number(formData.valor),
        };

        try {
            setLoading(true);
            await consumiblesAPI.create(payload);
            showToast('Insumo creado exitosamente', 'success');
            onCreated();
            onClose();
            // Reset form
            setFormData({
                nombre: '',
                categoria: 'Accesorios',
                descripcion: '',
                stockActual: 0,
                stockMinimo: 5,
                ubicacion: '',
                empresa: '',
                valor: '',
            });
        } catch (error) {
            console.error('Error al crear insumo:', error.message);
            showToast(error.response?.data?.message || 'Error al crear insumo', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md flex flex-col max-h-[90vh]">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                        <Package className="w-5 h-5 mr-2 text-blue-600" />
                        Nuevo Insumo
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 overflow-y-auto flex-1 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Nombre *
                        </label>
                        <input
                            type="text"
                            name="nombre"
                            value={formData.nombre}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500"
                            placeholder="Ej: Mouse Logitech M170"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Categoría *
                        </label>
                        <select
                            name="categoria"
                            value={formData.categoria}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="Accesorios">Accesorios</option>
                            <option value="Papelería">Papelería</option>
                            <option value="Toner">Toner</option>
                            <option value="Cables">Cables</option>
                            <option value="Periféricos">Periféricos</option>
                            <option value="Otros">Otros</option>
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Stock (Cantidad Inicial)
                            </label>
                            <input
                                type="number"
                                name="stockActual"
                                value={formData.stockActual}
                                onChange={handleChange}
                                min="0"
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Stock Mínimo (Alerta)
                            </label>
                            <input
                                type="number"
                                name="stockMinimo"
                                value={formData.stockMinimo}
                                onChange={handleChange}
                                min="0"
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Valor unitario (CLP)
                        </label>
                        <input
                            type="number"
                            name="valor"
                            value={formData.valor}
                            onChange={handleChange}
                            min="0"
                            step="1"
                            inputMode="numeric"
                            placeholder="Ej: 4500"
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <LocationSelector
                        value={formData.ubicacion}
                        onChange={(val) => handleSelectorChange('ubicacion', val)}
                    />

                    <EmpresaSelector
                        value={formData.empresa}
                        onChange={(val) => handleSelectorChange('empresa', val)}
                    />

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Descripción
                        </label>
                        <textarea
                            name="descripcion"
                            value={formData.descripcion}
                            onChange={handleChange}
                            rows="3"
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500"
                            placeholder="Detalles adicionales..."
                        />
                    </div>
                </form>

                <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center shadow-sm"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {loading ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </div>
        </div>
    );
}
